/** @jsxImportSource @emotion/react */
import React, { useState } from "react";
import {
  ColumnFiltersState,
  FilterFn,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useActivityColumnDefinitions } from "../../hooks/useActivityColumnDefinitions";
import { TransactionDetail } from "../../types/history";
import { TableRow } from "./elements/TableRow";
import { useTradeHistoryGroupedData } from "../../hooks/useTradeHistoryGroupedData";
import { NoFilterResultDisplay } from "../../pages/TradeHistory/elements/NoFilterResultDisplay";
import styled from "@emotion/styled";
import { useIsMobile } from "hooks/useDeviceType";
import { FilterBar } from "components/FilterBar/FilterBar";
import { Filter, FiltersInterface, NETWORK_NAME_TO_DISPLAY, TransactionType } from "config/consts";
import { Token } from "store/global/TokenEnum";
import { mainnet } from "viem/chains";

export const ActivityTable = () => {
  const colDefs = useActivityColumnDefinitions();
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const groupedData = useTradeHistoryGroupedData();
  const tableData = React.useMemo(
    () => (groupedData ? Object.values(groupedData).flat() : []),
    [groupedData]
  );
  const [confirmedOptions, setConfirmedOptions] = useState<FiltersInterface>(FILTERS_INITIAL_STATE);
  const isMobile = useIsMobile();

  const table = useReactTable<TransactionDetail>({
    data: tableData,
    columns: colDefs,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      columnFilters,
    },
    onColumnFiltersChange: setColumnFilters,
    filterFns: {
      network: networkFilter,
      transaction: transactionFilter,
      token: tokenFilter,
      amount: amountFilter,
    },
  });

  if (table.getFilteredRowModel().rows.length === 0) {
    return (
      <>
        <FilterBar
          table={table}
          confirmedOptions={confirmedOptions}
          setConfirmedOptions={setConfirmedOptions}
        />
        <NoResultContainer>
          <NoFilterResultDisplay
            clearAllFilter={() => {
              table.resetColumnFilters();
              setConfirmedOptions(FILTERS_INITIAL_STATE);
            }}
          />
        </NoResultContainer>
      </>
    );
  }

  return (
    <>
      <FilterBar
        table={table}
        confirmedOptions={confirmedOptions}
        setConfirmedOptions={setConfirmedOptions}
      />
      <TableContainer isMobile={isMobile}>
        <Table isMobile={isMobile}>
          <TableBody>
            {table.getRowModel().rows.map((row, rowIndex, arr) => {
              const currMonth = row.original.timestamp.getMonth();
              const prevRow = arr[rowIndex - 1];
              const nextRow = arr[rowIndex + 1];
              const prevMonth = prevRow ? prevRow.original.timestamp.getMonth() : null;
              const nextMonth = nextRow ? nextRow.original.timestamp.getMonth() : null;
              const firstTxOfMonth = currMonth !== prevMonth;
              const lastTxOfMonth = currMonth !== nextMonth;

              return (
                <TableRow
                  key={rowIndex.toString()}
                  row={row}
                  firstTxOfMonth={firstTxOfMonth}
                  lastTxOfMonth={lastTxOfMonth}
                  rowIndex={rowIndex}
                />
              );
            })}
          </TableBody>
          <tfoot>
            {table.getFooterGroups().map(footerGroup => (
              <tr key={footerGroup.id}>
                {footerGroup.headers.map(header => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.footer, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </tfoot>
        </Table>
      </TableContainer>
    </>
  );
};

// Sub-components

const TableContainer = styled.div<{ isMobile: boolean }>`
  height: ${props => (props.isMobile ? "89%" : "auto")};
  overflow-y: ${props => (props.isMobile ? "scroll" : "hidden")};
  overflow-x: hidden;
  scrollbar-width: thin;

  ::-webkit-scrollbar {
    width: 6px;
  }
`;

const Table = styled.table<{ isMobile: boolean }>`
  border-collapse: collapse;
  width: 100%;
  display: table;
  caption-side: top;
  padding-right: ${props => (props.isMobile ? "8px" : "0")};
`;

const TableBody = styled.tbody`
  height: inherit;
  display: table-cell;
  position: relative;
  z-index: 0;
`;

const NoResultContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: auto;
`;

// Helpers

const networkFilter: FilterFn<TransactionDetail> = (
  row,
  columnId,
  value: FiltersInterface["network"]
) => {
  const networkId: number = row.getValue(columnId);
  const networkName = NETWORK_NAME_TO_DISPLAY[networkId] ?? mainnet.name;
  // value = ["Arbitrum", "Polygon", "Ethereum"]
  return value?.includes(networkName) ?? false;
};

const transactionFilter: FilterFn<TransactionDetail> = (
  row,
  columnId,
  value: FiltersInterface["transaction"]
) => {
  const transaction: {
    sender: TransactionDetail["sender"];
    receiver: TransactionDetail["receiver"];
  } = row.getValue(columnId);
  const transactionType = getTransactionType(transaction.sender, transaction.receiver);
  return value?.includes(transactionType) ?? false;
};

const tokenFilter: FilterFn<TransactionDetail> = (
  row,
  columnId,
  value: FiltersInterface["token"]
) => {
  const tokens: Array<Token> = row.getValue(columnId);
  return value?.some(token => tokens.includes(token)) ?? false;
};

const amountFilter: FilterFn<TransactionDetail> = (
  row,
  columnId,
  value: FiltersInterface["amount"]
) => {
  const amount: TransactionDetail["volumeUsd"] = row.getValue(columnId);
  if (value == null) return true;
  if (value.min != null && amount.value.lessThan(value.min.value)) return false;
  if (value.max != null && amount.value.greaterThan(value.max.value)) return false;
  return true;
};

function getTransactionType(
  sender: TransactionDetail["sender"],
  receiver: TransactionDetail["receiver"]
) {
  if (sender !== receiver) return TransactionType.TradeAndSend;
  return TransactionType.Trade;
}

export const FILTERS_INITIAL_STATE = {
  [Filter.Network]: undefined,
  [Filter.Transaction]: undefined,
  [Filter.Token]: undefined,
  [Filter.Amount]: undefined,
  [Filter.Date]: undefined,
};
